import { delay, http, HttpResponse } from "msw";
import { apiPaths } from "../../api-paths.js";
import type {
  Checkout_GetCheckout_CheckoutDealPricing,
  Checkout_GetCheckout_ComplexPrice,
  Checkout_GetCheckout_Money,
  Trips_GetModificationCheckout_CancellationPenalty,
  Trips_GetModificationCheckout_CancellationPolicy,
  Trips_GetModificationCheckout_Deal,
  Trips_GetModificationCheckout_GetModificationCheckoutQueryResponse_NotFound,
  Trips_GetModificationCheckout_GetModificationCheckoutQueryResponse_Unprocessable,
  Trips_GetModificationCheckout_GetModificationCheckoutQueryResponse_Success,
  Trips_GetModificationCheckout_Occupancy,
} from "../../swagger-types/v1/index.js";

interface ModificationCheckoutHandlerFactoryOptions {
  delayMs?: number | "infinite";
}

const waitForFactoryDelay = async ({ delayMs }: ModificationCheckoutHandlerFactoryOptions = {}) => {
  if (delayMs === "infinite") {
    await delay("infinite");
    return;
  }

  if (typeof delayMs === "number" && delayMs > 0) {
    await delay(delayMs);
  }
};

const waitUntilRequestAborted = async (request: Request) => {
  await new Promise<void>((resolve) => {
    request.signal.addEventListener("abort", () => resolve(), { once: true });
  });
};

const createMoney = (amount: number, currency = "USD"): Checkout_GetCheckout_Money => ({
  amount,
  currency,
});

const createComplexPrice = (total: number, currencyCode = "USD"): Checkout_GetCheckout_ComplexPrice => ({
  total,
  currencyCode,
  items: [],
});

const createPricing = (totalPrice: number, currency = "USD"): Checkout_GetCheckout_CheckoutDealPricing => ({
  fees: createComplexPrice(12, currency),
  taxes: createComplexPrice(24, currency),
  hotelFees: createComplexPrice(18, currency),
  hotelFeeTaxes: createComplexPrice(3, currency),
  currency,
  nightlyPrice: totalPrice / 3,
  totalRoomPrice: totalPrice,
  totalRoomsPrice: totalPrice,
  totalPriceWithHotelFees: totalPrice + 21,
});

const createCancellationPolicy = (): Trips_GetModificationCheckout_CancellationPolicy => ({
  type: "Refundable",
  deadline: "2026-12-15T23:59:59Z",
  description: "Free cancellation until deadline",
  penalties: [
    {
      from: "2026-12-16T00:00:00Z",
      to: null,
      amount: createMoney(150),
    } satisfies Trips_GetModificationCheckout_CancellationPenalty,
  ],
});

const createModificationCheckoutDeal = (index = 0, priceDelta = 25): Trips_GetModificationCheckout_Deal => {
  const totalPrice = 360;

  return {
    checkoutId: `deal-${index + 1}`,
    roomName: "Standard Room",
    roomCode: "1",
    boardCode: "RoomOnly",
    boardDescription: "Room Only",
    checkIn: "2026-12-18",
    checkOut: "2026-12-21",
    cancellationPolicy: createCancellationPolicy(),
    pricing: createPricing(totalPrice),
    paymentPricing: createPricing(totalPrice),
    hotelCurrencyDealPricing: null,
    totalPriceWithoutHotelFeesPriceDelta: createMoney(priceDelta),
    hotelFeesPriceDelta: createMoney(8),
  };
};

const createModificationCheckoutMock = (
  deal: Trips_GetModificationCheckout_Deal = createModificationCheckoutDeal(),
): Trips_GetModificationCheckout_GetModificationCheckoutQueryResponse_Success => {
  const occupancies: Trips_GetModificationCheckout_Occupancy[] = [
    {
      adults: 2,
      children: [],
    },
  ];

  return {
    deal,
    occupancies,
    checkoutPaymentProvider: {
      isPaymentInDifferentCurrency: false,
      isTaxIdRequired: false,
      providerCode: "Stripe",
      providerMetadata:
        '{"PublicKey":"pk_test_51PxCceBFjgT4ftd9kR8JsnMsskfxUBiTSGFXlj9QMJXpBEigrJGmdUnm7hosqkzWzlps4mKvxtZ32EBhqP98MuM000O9rIhhAX","CardTypeMapping":{"amex":"AmericanExpress","visa":"Visa","mastercard":"MasterCard","diners":"Diners","jcb":"JCB","discover":"Discover","unionpay":"UnionPay","unknown":"Unknown"}}',
    },
  };
};

const createModificationCheckout200Handler = (
  response: Trips_GetModificationCheckout_GetModificationCheckoutQueryResponse_Success = createModificationCheckoutMock(),
  options: ModificationCheckoutHandlerFactoryOptions = {},
) => [
  http.post(apiPaths.trips.modification.getCheckout, async () => {
    await waitForFactoryDelay(options);
    return HttpResponse.json(response, { status: 200 });
  }),
];

const createModificationCheckout404Handler = [
  http.post(apiPaths.trips.modification.getCheckout, async () => {
    const responseBody: Trips_GetModificationCheckout_GetModificationCheckoutQueryResponse_NotFound = {};
    return HttpResponse.json(responseBody, { status: 404 });
  }),
];

const createModificationCheckout422Handler = [
  http.post(apiPaths.trips.modification.getCheckout, async () => {
    const responseBody: Trips_GetModificationCheckout_GetModificationCheckoutQueryResponse_Unprocessable = {};
    return HttpResponse.json(responseBody, { status: 422 });
  }),
];

const createModificationCheckout500Handler = [
  http.post(apiPaths.trips.modification.getCheckout, async () => {
    return HttpResponse.json({}, { status: 500 });
  }),
];

const createModificationCheckoutLoadingHandler = [
  http.post(apiPaths.trips.modification.getCheckout, async ({ request }) => {
    await waitUntilRequestAborted(request);
    return HttpResponse.json(createModificationCheckoutMock(), { status: 200 });
  }),
];

export const modificationCheckoutHandlers = {
  get200: createModificationCheckout200Handler(),
  get404: createModificationCheckout404Handler,
  get422Unprocessable: createModificationCheckout422Handler,
  get500: createModificationCheckout500Handler,
  getLoading: createModificationCheckoutLoadingHandler,
} as const;

export const modificationCheckoutHandlersFactory = {
  create200: (
    response?: Trips_GetModificationCheckout_GetModificationCheckoutQueryResponse_Success,
    options?: ModificationCheckoutHandlerFactoryOptions,
  ) => createModificationCheckout200Handler(response, options),
  createMock: createModificationCheckoutMock,
  createDeal: createModificationCheckoutDeal,
} as const;
